'use client';

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Gift, Loader2, CheckCircle2, Wallet } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { trpc } from '@/lib/api/trpc';

function formatUsd(cents: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

function ledgerLabel(
  t: (key: string, opts?: Record<string, unknown>) => string,
  type: string,
): string {
  switch (type) {
    case 'promo_redeem':
      return t('credits.ledger.types.promoRedeem');
    case 'admin_grant':
      return t('credits.ledger.types.adminGrant');
    case 'billing_apply':
      return t('credits.ledger.types.billingApply');
    case 'clawback':
      return t('credits.ledger.types.clawback');
    case 'referral_welcome':
      return t('credits.ledger.types.referralWelcome');
    case 'referral_conversion':
      return t('credits.ledger.types.referralConversion');
    default:
      return type;
  }
}

export function CreditsTab() {
  const { t } = useTranslation('billing');
  const [searchParams, setSearchParams] = useSearchParams();
  const codeFromUrl = searchParams.get('code') ?? '';

  const [code, setCode] = useState(codeFromUrl);
  const [justRedeemed, setJustRedeemed] = useState<{
    amountCents: number;
    balanceCents: number;
    code: string;
  } | null>(null);

  useEffect(() => {
    if (codeFromUrl) setCode(codeFromUrl);
  }, [codeFromUrl]);

  const balanceQuery = trpc.billingCredits.getBalance.useQuery();
  const ledgerQuery = trpc.billingCredits.listLedger.useQuery({ limit: 25, offset: 0 });
  const redeemMutation = trpc.billingCredits.redeem.useMutation({
    onSuccess: (result) => {
      setJustRedeemed({
        amountCents: result.amountCents,
        balanceCents: result.balanceCents,
        code: result.code,
      });
      toast.success(t('credits.toasts.redeemSuccess', {
        amount: formatUsd(result.amountCents),
      }));
      void balanceQuery.refetch();
      void ledgerQuery.refetch();

      // Drop code from URL after successful redeem to avoid re-submit UX
      if (searchParams.get('code')) {
        const next = new URLSearchParams(searchParams);
        next.delete('code');
        setSearchParams(next, { replace: true });
      }
      setCode('');
    },
    onError: (err) => {
      toast.error(err.message || t('credits.toasts.redeemFailed'));
    },
  });

  const balanceCents = balanceQuery.data?.balanceCents ?? 0;

  const canSubmit = useMemo(
    () => code.trim().length > 0 && !redeemMutation.isPending,
    [code, redeemMutation.isPending],
  );

  const handleRedeem = () => {
    if (!canSubmit) return;
    setJustRedeemed(null);
    redeemMutation.mutate({ code: code.trim() });
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Wallet className="h-4 w-4" />
              {t('credits.balance.title')}
            </CardTitle>
            <CardDescription>{t('credits.balance.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            {balanceQuery.isLoading ? (
              <div className="text-muted-foreground flex items-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('common.loading')}
              </div>
            ) : (
              <div className="text-3xl font-semibold tracking-tight">
                {formatUsd(balanceCents)}
              </div>
            )}
            <p className="text-muted-foreground mt-2 text-sm">
              {t('credits.balance.hint')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Gift className="h-4 w-4" />
              {t('credits.redeem.title')}
            </CardTitle>
            <CardDescription>{t('credits.redeem.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder={t('credits.redeem.placeholder')}
                autoFocus={Boolean(codeFromUrl)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRedeem();
                }}
                className="font-mono uppercase tracking-wide"
              />
              <Button onClick={handleRedeem} disabled={!canSubmit}>
                {redeemMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('credits.redeem.activating')}
                  </>
                ) : (
                  t('credits.redeem.cta')
                )}
              </Button>
            </div>

            {justRedeemed && (
              <div className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800 dark:border-green-900 dark:bg-green-950/40 dark:text-green-200">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <p className="font-medium">
                    {t('credits.redeem.successTitle', {
                      amount: formatUsd(justRedeemed.amountCents),
                    })}
                  </p>
                  <p>
                    {t('credits.redeem.successBody', {
                      balance: formatUsd(justRedeemed.balanceCents),
                      code: justRedeemed.code,
                    })}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('credits.ledger.title')}</CardTitle>
          <CardDescription>{t('credits.ledger.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          {ledgerQuery.isLoading ? (
            <div className="text-muted-foreground flex items-center gap-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('common.loading')}
            </div>
          ) : (ledgerQuery.data?.entries.length ?? 0) === 0 ? (
            <p className="text-muted-foreground text-sm">{t('credits.ledger.empty')}</p>
          ) : (
            <div className="overflow-hidden rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('credits.ledger.columns.date')}</TableHead>
                    <TableHead>{t('credits.ledger.columns.type')}</TableHead>
                    <TableHead>{t('credits.ledger.columns.description')}</TableHead>
                    <TableHead className="text-right">{t('credits.ledger.columns.amount')}</TableHead>
                    <TableHead className="text-right">{t('credits.ledger.columns.balance')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ledgerQuery.data?.entries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="whitespace-nowrap text-sm">
                        {entry.createdAt
                          ? new Date(entry.createdAt).toLocaleString()
                          : t('common.notAvailable')}
                      </TableCell>
                      <TableCell className="text-sm">
                        {ledgerLabel(t, entry.type)}
                      </TableCell>
                      <TableCell className="text-muted-foreground max-w-[280px] truncate text-sm">
                        {entry.description || '—'}
                      </TableCell>
                      <TableCell
                        className={`text-right text-sm font-medium ${
                          entry.amountCents >= 0 ? 'text-green-700' : 'text-foreground'
                        }`}
                      >
                        {entry.amountCents >= 0 ? '+' : ''}
                        {formatUsd(entry.amountCents)}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {formatUsd(entry.balanceAfterCents)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
